<template>
    <v-container fluid v-if="activeProject">
        <v-card class="mb-3 py-1">
            <v-card-text>
                <div class="title">Bot {{activeProject.bot_name}} - Agent Overview</div>
                <v-layout class="ma-1" align-center wrap>
                    <span class="mr-3">
                    <v-autocomplete
                        v-model="channel"
                        :label="$filters.capitalize($t('channel'))"
                        :items="channelList"
                        clearable
                    ></v-autocomplete>
                </span>
                    <span class="mr-3">
                    <DatePicker
                        v-model="fromDate"
                        :label="$filters.capitalize($t('from'))"
                        :max="toDate"
                    ></DatePicker>
                </span>
                    <span>
                    <DatePicker
                        v-model="toDate"
                        :label="$filters.capitalize($t('to'))"
                        :min="fromDate"
                        :max="today"
                    ></DatePicker>
                </span>
                    <v-spacer></v-spacer>
                    <v-btn depressed color="#878787" dark @click="fetchData" class="text-capitalize">
                        <v-icon class="mr-1">mdi-sync</v-icon>
                        {{$t('update')}}
                    </v-btn>
                    <v-btn depressed color="#42BE80" dark @click="getAgentReportOverviewExcel" class="text-capitalize">
                        <v-icon class="mr-1">mdi-file-excel</v-icon>
                        {{$t('exportReport')}}
                    </v-btn>
                </v-layout>
            </v-card-text>
        </v-card>
        <v-card class="mb-3 pa-3">
            <div class="title mb-3">
                Overall
            </div>
            <TimelineChart :data-sets="ratingBarData" :labels="ratingBarLabels" :is-bot="false"></TimelineChart>
        </v-card>
        <v-layout class="mb-3">
            <v-flex xs6 class="pr-2">
                <v-card class="pa-3">
                    <div class="title">
                        Agent Rating
                    </div>
                    <OverallChartRating :dataSets="ratingAgentPieData" :labels="ratingAgentPieLabels"></OverallChartRating>
                </v-card>
            </v-flex>
            <v-flex xs6 class="pl-2">
                <v-card class="pa-3">
                    <div class="title">
                        Agent's Message Reaction
                    </div>
                    <OverallChartRating :data-sets="ratingAnswerPieData" :labels="ratingAnswerPieLabels" :color-reaction="true"></OverallChartRating>
                </v-card>
            </v-flex>
        </v-layout>
        <v-card>
            <SimpleDataTable
                :headers="headers"
                :items="dataTable"
                :pagination.sync="pagination"
            ></SimpleDataTable>
        </v-card>
    </v-container>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'
import DatePicker from '@/components/DatePicker'
import TimelineChart from '@/views/Agent/TimelineChart'
import OverallChartRating from '@/views/Agent/OverallChartRating'
import SimpleDataTable from '@/components/SimpleDataTable'
import DataTableMixin from '@/mixins/DataTableMixin'
import AgentService from '@/services/agent.service'
import AnswerService from '@/services/answer.service'
import CommonService from '@/services/common.service'
import _ from 'lodash'

export default {
    name: 'AgentOverview',
    components: { DatePicker, TimelineChart, OverallChartRating, SimpleDataTable },
    mixins: [DataTableMixin],
    data () {
        return {
            channel: '',
            agent_id: '',
            agentList: [
                { text: 'agent 1', value: 'agent 1' },
                { text: 'agent 2', value: 'agent 2' },
                { text: 'agent 3', value: 'agent 3' },
                { text: 'agent 4', value: 'agent 4' }
            ],
            channelList: this.$const.channelList,
            fromDate: moment().subtract(1, 'month').format('YYYY-MM-DD'),
            toDate: moment().format('YYYY-MM-DD'),
            today: moment().format('YYYY-MM-DD'),
            ratingAgentPieData: [],
            ratingAgentBarData: [],
            ratingAnswerPieData: [],
            ratingAnswerBarData: [],
            ratingAgentPieLabels: ['1 star  ', '2 stars', '3 stars', '4 stars', '5 stars'],
            ratingAnswerPieLabels: ['Like', 'Dislike'],
            ratingBarData: [],
            ratingBarLabels: []
        }
    },
    computed: {
        ...mapState({
            activeProject: state => state.user.activeProject
        }),
        headers () {
            return this.$appService.correctHeaders([
                {
                    text: this.$t('name'),
                    value: 'agent_name'
                },
                {
                    text: this.$t('email'),
                    value: 'agent_email'
                },
                {
                    text: this.$t('ratedTimes'),
                    value: 'rate_time'
                },
                {
                    text: this.$t('stars'),
                    value: 'avg_star'
                }
            ])
        },
        filter () {
            return {
                channel: this.channel,
                fromDate: this.fromDate,
                toDate: this.toDate
            }
        }
    },
    methods: {
        getDataTablePromise () {
            return AgentService.getAgentOverviewReportTable(this.channel, this.fromDate, this.toDate, this.pagination)
        },
        getAgentReportPieChart () {
            AgentService.getAgentReportPieChart(this.channel, this.fromDate, this.toDate).then(
                result => {
                    let _ratingAgentPieData = []
                    _.forEach(result.data, function (value, key) {
                        if (key !== 'avg_star' && key !== 'total_rate') {
                            _ratingAgentPieData.push(value)
                        }
                    })
                    this.ratingAgentPieData = _ratingAgentPieData
                })
        },
        getAnswerReportPieChart () {
            AnswerService.getAnswerReportPieChart('agent', this.channel, this.fromDate, this.toDate).then(
                result => {
                    let _ratingAnswerPieData = []
                    _.forEach(result.data, function (value, key) {
                        if (key !== 'total_rate') {
                            _ratingAnswerPieData.push(value)
                        }
                    })
                    this.ratingAnswerPieData = _ratingAnswerPieData
                }
            )
        },
        getOverviewReportBarChart () {
            Promise.all([
                AnswerService.getAgentAnswerReportBarChart(this.channel, this.fromDate, this.toDate),
                AgentService.getAgentReportBarChart(this.channel, this.fromDate, this.toDate)
            ]).then(
                results => {
                    let answerReportData = results[0].data
                    let agentReportData = results[1].data
                    let dataReport = CommonService.getRateDataChart(agentReportData, answerReportData)
                    this.ratingBarLabels = dataReport.labels
                    this.ratingBarData = dataReport.dataSets
                }
            )
        },
        getAgentReportOverviewExcel () {
            return AgentService.getAgentOverviewReportExcel(this.agent_id, this.channel, this.fromDate, this.toDate)
        },
        fetchData () {
            this.getAnswerReportPieChart()
            this.loadDataTable()
            this.getAgentReportPieChart()
            this.getOverviewReportBarChart()
        }
    },
    watch: {
        activeProject (val) {
            if (val) {
                this.fetchData()
            }
        },
        filter () {
            this.fetchData()
        },
        '$route': {
            handler () {
                if (this.activeProject) {
                    this.fetchData()
                }
            },
            immediate: true,
            deep: true
        }
    }
}
</script>

<style scoped>

</style>
